import { IdentifierDTO } from '@zazume/zzm-base';

import { ShortLink } from '#/domain/shortLink/ShortLink';
import { PaginationOptions } from '#/hooks';
import {
  AvailabilitySlots,
  BasicUnit,
  BasicUserVO,
  Document,
  Staff,
  Tenant,
  Unit,
  UnitNotesBody,
  UnitPortalRequestBody,
  User
} from '#/models';
import { AgencySearchResult } from '#/models/agency/Agency';
import { Availability } from '#/models/Availability';
import { Deletable } from '#/models/common';
import { OwnerAccountAndLiteUnitFormSchema } from '#/pages/people/ownerAccount/components/addOwnerAccountAndLiteUnitModal/components/AddOwnerAccountAndLiteUnitForm.schema';
import { EditApplicantQualificationFormInputs } from '#/pages/workspaces/units/components/editQualificationModal/EditApplicantQualification.schema';
import { CreateUploadLinkForm } from '#/pages/workspaces/units/components/unitDetail/aboutTab/unitPhotosSection/uploadImagesModal/uploadLinks/createUploadLinkModal/Form.schema';
import { OwnerAssignmentForm } from '#/pages/workspaces/units/components/unitDetail/peopleTab/components/modals/OwnerAssignmentModal.schema';
import { UpdateRentalConditionsRequestBody } from '#lib/network/api/unit/models/UpdateRentalConditionsRequestBody';

import {
  doDelete,
  doGet,
  doPost,
  doPostFileUpload,
  doPut,
  doPutFileUpload,
  EndpointBuilder,
  parserDataList,
  toPaginatedURL
} from '../../helpers';
import { UpdateDocumentRequest } from '../documents/requests/UpdateDocumentRequest';
import { BulkAssignManagerRequest } from './models/BulkAssignManagerRequest';
import { CreateUnitDocumentRequest } from './models/CreateUnitDocumentRequest';

const toBasicUser = (rawBasicUser: any): BasicUserVO => new BasicUserVO({ ...rawBasicUser });

export const units = {
  //TODO: Following method getByWorkspace should be avoid because it does not have any pagination
  //TODO: Create a non paginated method with a selected limit.
  assignAgent: (unitId: IdentifierDTO) => (userId: IdentifierDTO) => doPost(`/units/${unitId}/agent/${userId}`),
  assignProvidedBy: (unitId: IdentifierDTO) => userId => doPut<void>(`/units/${unitId}/providedBy/${userId}`),
  assingAccountManager: (unitId: IdentifierDTO) => userId => doPost(`/units/${unitId}/account-manager`, { userId }),
  assingIncidentsManager: (unitId: IdentifierDTO) => incidentsManagerId =>
    doPost(`/units/${unitId}/incidents-manager`, { incidentsManagerId }),
  assingManager: (unitId: IdentifierDTO) => (manager: Partial<User>) =>
    doPost(`/units/${unitId}/manager`, { managerId: manager._id }),
  assingOwnerAccount: (unitId: IdentifierDTO) => (ownerAssignmentForm: Partial<OwnerAssignmentForm>) =>
    doPost(`/units/${unitId}/owner-account`, ownerAssignmentForm),
  assingTenant: (unitId: IdentifierDTO) => (tenant: Partial<Tenant>) =>
    doPost(`/units/${unitId}/tenant`, { tenantId: tenant._id }),
  assingZazu: (unitId: IdentifierDTO) => (zazu: Partial<Staff>) =>
    doPost(`/units/${unitId}/zazu`, { zazuId: zazu._id }),
  attachHeroImage: (unitId: IdentifierDTO) => (heroImage: FormData) =>
    doPostFileUpload(`/units/${unitId}/heroImage`, heroImage),
  blockPayouts: (unitId: IdentifierDTO) => doPut(`/units/block-payouts/${unitId}`),
  bulkActions: {
    assignManager: (organizationId: IdentifierDTO) => (body: BulkAssignManagerRequest) =>
      doPut<void>(`/units/bulk-actions/assign-manager/${organizationId}`, body)
  },
  canBeDeleted: (unitId: IdentifierDTO) => doGet<Deletable>(`/onboarding/unit/${unitId}`),
  create: () => (unit: Partial<Unit>) => doPost('/units', unit),
  createAvailability: (unitId: IdentifierDTO) => body => doPost(`/availabilities/unit/${unitId}`, body),
  createDocument: (unitId: IdentifierDTO) => (request: CreateUnitDocumentRequest) =>
    doPostFileUpload<void>(`/units/documents/${unitId}/`, request.build()),
  createLite: (body: OwnerAccountAndLiteUnitFormSchema) => doPost('/units/lite', body),
  createUploadPhotosLink: (unitId: IdentifierDTO) => (request: CreateUploadLinkForm) =>
    doPost<ShortLink>(`/dashboard/unit/upload-photos-link/${unitId}`, request),
  delete: (unitId: IdentifierDTO) => doDelete<Unit>(`/units/${unitId}`),
  deleteAllPhotos: (unitId: IdentifierDTO) => () => doDelete(`/units/${unitId}/photos`),
  deleteHeroImage: (unitId: IdentifierDTO) => (unit: Partial<Unit>) => doDelete<Unit>(`/units/${unitId}/heroImage`),
  deletePhoto: (unitId: IdentifierDTO) => photoId => doDelete<any>(`/units/${unitId}/photos/${photoId}`),
  deleteVideo: (unitId: IdentifierDTO) => videoId => doDelete<any>(`/units/${unitId}/videos/${videoId}`),
  deleteVirtualTour: (unitId: IdentifierDTO) => doDelete<any>(`/units/${unitId}/virtual-tour`),
  edit: (unitId: IdentifierDTO) => (unit: Partial<Unit>) => doPut<Unit>(`/units/${unitId}`, unit),
  findAccountManagersInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUserVO[]>(`/units/organization/${organizationId}/account-manager`, parserDataList(toBasicUser)),
  findAgenciesInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<AgencySearchResult[]>(`/units/organization/${organizationId}/agency`),
  findAgentsInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUserVO[]>(`/units/organization/${organizationId}/agent`, parserDataList(toBasicUser)),
  //TODO: Check if can be removed and use other end point where is being used.
  findIncidentsManagersInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUserVO[]>(`/units/organization/${organizationId}/incidents-manager`, parserDataList(toBasicUser)),
  findManagersInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUserVO[]>(`/units/organization/${organizationId}/manager`, parserDataList(toBasicUser)),
  findProvidedByInUnitsByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUserVO[]>(`/units/organization/${organizationId}/provided-by`, parserDataList(toBasicUser)),
  get: (unitId: IdentifierDTO) => () => doGet<Unit>(`/units/${unitId}`),
  getAvailabilityById: (availabilityId: IdentifierDTO) => () =>
    doGet<Availability>(`/availabilities/unit/availability/${availabilityId}`),
  getAvailabilityByUnitId: (unitId: IdentifierDTO) => () => doGet<Availability>(`/availabilities/unit/${unitId}`),
  getAvailabilitySlots: (availabilityId: IdentifierDTO) => () =>
    doGet<AvailabilitySlots>(`/availabilities/unit/slots/${availabilityId}`),
  getBasic: (unitId: IdentifierDTO) => () => doGet<BasicUnit>(`/dashboard/unit/basic-unit/${unitId}`),
  getByOrganization:
    (organizationId: IdentifierDTO, unitLimit = 100) =>
    () =>
      doGet<Unit[]>(toPaginatedURL(`/units/organization/${organizationId}`, { page: 1, size: unitLimit })),
  getByWorkspace:
    (organizationId: IdentifierDTO, workspaceId: IdentifierDTO, unitLimit = 100) =>
    () =>
      doGet<Unit[]>(
        toPaginatedURL(`/units/organization/${organizationId}/workspace/${workspaceId}`, { page: 1, size: unitLimit })
      ),
  getDocuments: (unitId: IdentifierDTO, types?: string) => () =>
    doGet<Document[]>(`/units/documents/${unitId}?types=${types ? types : ''}`),
  getEmptyOrganizationUnits: (organizationId: IdentifierDTO) => () =>
    doGet<Unit[]>(`/units/organization/${organizationId}/search?&status=empty`),
  getOrganizationUnits: (organizationId: IdentifierDTO) => () =>
    doGet<Unit[]>(`/units/organization/${organizationId}/search?`),
  getPaginated: (params: EndpointBuilder) => (pagination: PaginationOptions) => () =>
    doGet<Unit[]>(toPaginatedURL(`/units${params.url}${params.queryParams}`, pagination)),
  getRecentByOrganization: (organizationId: IdentifierDTO) => () =>
    doGet<Unit[]>(`/units/organization/${organizationId}/search`),
  getUploadPhotosLink: (unitId: IdentifierDTO) => () =>
    doGet<ShortLink[]>(`/dashboard/unit/upload-photos-link/${unitId}`),
  getVideoPresignedPostURL: (id: IdentifierDTO) => (contentType: string, filename: string) =>
    doPost<{ url: string; fields: Record<string, string>; publicURL: string }>(`/units/to-upload-url/${id}`, {
      contentType,
      filename
    }),
  requestServiceUpgrade: (unitId: IdentifierDTO) => body => doPost(`/units/${unitId}/upgrade`, body),
  saveInmofactoryPortalConfiguration: (unitId: IdentifierDTO) =>
    doPut<Unit>(`/units/${unitId}/portal-configuration/inmofactory`),
  savePortalConfiguration: (unitId: IdentifierDTO) => (portal: UnitPortalRequestBody) =>
    doPut<Unit>(`/units/${unitId}/portal-configuration`, portal),
  searchEmptyOrganizationUnitsByName: (organizationId: IdentifierDTO) => (name: string) => () =>
    doGet<Unit[]>(`/units/organization/${organizationId}/search?name=${name}&status=empty`),
  searchOrganizationUnitsByName: (organizationId: IdentifierDTO) => (name: string) => () =>
    doGet<Unit[]>(`/units/organization/${organizationId}/search?name=${name}`),
  sortPhotos: (unitId: IdentifierDTO) => (data: any) => doPost(`/units/${unitId}/photos/sort`, data),
  aiSortPhotos: (unitId: IdentifierDTO) => () => doPost<void>(`/units/${unitId}/photos/ai-sort`),
  getAiSortStatus: (unitId: IdentifierDTO) => () =>
    doGet<{ status: 'idle' | 'processing' | 'completed' | 'error' | 'cancelled' }>(
      `/units/${unitId}/photos/ai-sort/status`
    ),
  cancelAiSortPhotos: (unitId: IdentifierDTO) => () => doPost<void>(`/units/${unitId}/photos/ai-sort/cancel`),
  undoAiSortPhotos: (unitId: IdentifierDTO) => () => doPost<Unit>(`/units/${unitId}/photos/ai-sort/undo`),
  unassignAgent: (unitId: IdentifierDTO) => (userId: IdentifierDTO) => doDelete(`/units/${unitId}/agent/${userId}`),
  unassignProvidedBy: (unitId: IdentifierDTO) => userId => doDelete<void>(`/units/${unitId}/providedBy/${userId}`),
  unassingAccountManager: (unitId: IdentifierDTO) => userId => doDelete(`/units/${unitId}/account-manager`, { userId }),
  unassingIncidentsManager: (unitId: IdentifierDTO) => incidentsManagerId =>
    doDelete(`/units/${unitId}/incidents-manager`, { incidentsManagerId }),
  unassingManager: (unitId: IdentifierDTO) => (managerId: IdentifierDTO) =>
    doDelete(`/units/${unitId}/manager`, { managerId: managerId }),
  unassingOwnerAccount: (unitId: IdentifierDTO) => (ownerAccountId: IdentifierDTO) =>
    doDelete(`/units/${unitId}/owner-account`, { ownerAccountId: ownerAccountId }),
  unassingOwnerLead: (unitId: IdentifierDTO) => (ownerLeadId: IdentifierDTO) =>
    doDelete(`/units/${unitId}/owner-lead`, { ownerLeadId: ownerLeadId }),
  unassingTenant: (unitId: IdentifierDTO) => (tenantId: IdentifierDTO) =>
    doDelete(`/units/${unitId}/tenant`, { tenantId: tenantId }),
  unassingZazu: (unitId: IdentifierDTO) => (zazuId: IdentifierDTO) =>
    doDelete(`/units/${unitId}/zazu`, { zazuId: zazuId }),
  unblockPayouts: (unitId: IdentifierDTO) => doPut(`/units/unblock-payouts/${unitId}`),
  updateAvailability: (availabilityId: IdentifierDTO) => body => doPut(`/availabilities/unit/${availabilityId}`, body),
  updateDocument: (unitId: IdentifierDTO, documentId: IdentifierDTO) => (request: UpdateDocumentRequest) =>
    doPutFileUpload<void>(`/units/documents/${unitId}/${documentId}`, request.build()),
  updateNotes: (unitId: IdentifierDTO) => (body: Partial<UnitNotesBody>) => doPut<Unit>(`/units/${unitId}/notes`, body),
  updateOwnerPreScoring: (unitId: IdentifierDTO) => (preScoring: EditApplicantQualificationFormInputs) =>
    doPut(`/units/${unitId}/pre-scoring`, preScoring),
  updatePhotoTag: (unitId: IdentifierDTO, photoId: IdentifierDTO) => (body: any) =>
    doPut<any>(`/units/${unitId}/photos/${photoId}`, body),
  updateRentalConditions: (unitId: IdentifierDTO) => (body: UpdateRentalConditionsRequestBody) =>
    doPut<Unit>(`/units/${unitId}/rental-conditions`, body),
  updateVirtualTour: (unitId: IdentifierDTO) => (virtualTour: any) =>
    doPut<any>(`/units/${unitId}/virtual-tour`, { virtualTour }),
  uploadPhotos: (unitId: IdentifierDTO) => (data: any) =>
    doPostFileUpload<Unit>(`/units/${unitId}/photos`, data.formData),
  uploadVideos: (unitId: IdentifierDTO) => (data: { body: { videos: any[] } }) =>
    doPut<Unit>(`/units/${unitId}/videos`, data.body),
  validatePortals: (unitId: IdentifierDTO) => () => doGet<any>(`/units/${unitId}/validate/portals`)
};
